import { Loading } from '@/components/Loading'
import { getPageStaticPropsForPath } from '@/lib/getPageStaticPropsForPath'
import { shouldSlugBeRedirected } from '@/utils'
import { cleanJson } from '@/utils/cleanJson'
import { isWebLandingPage } from '@/utils/type_predicates'
import type { GetStaticPaths, GetStaticProps } from 'next'
import Error from 'next/error'
import { useRouter } from 'next/router'
import UnknownComponent from '../components/UnknownComponent'
import pageLayouts from '../layouts'

function Page(props) {
	const router = useRouter()

	// If the page is not yet generated, this will be displayed
	// initially until getStaticProps() finishes running
	if (router.isFallback) {
		return <Loading />
	}

	if (!props) return null

	if (props.errorCode || !props?.data?.pageResponse?.item?.system?.type) {
		return <Error statusCode={props.errorCode} />
	}

	// every page can have different layout, pick the layout based on content type
	const contentType = props.data.pageResponse.item.system.type

	const PageLayout = pageLayouts[contentType]

	if (process.env.NODE_ENV === 'development' && !PageLayout) {
		return (
			<UnknownComponent {...props} useLayout={true}>
				<p>
					Unknown Layout component for page content type:{' '}
					{contentType}
				</p>
			</UnknownComponent>
		)
	}

	return <PageLayout {...props} />
}

export const getStaticPaths: GetStaticPaths = async () => {
	// const allPaths = await getSiteMappings()

	// // Exclude the redirected-to-home paths
	// const paths = allPaths
	// 	.filter((path) => {
	// 		// render if
	// 		return (
	// 			path.params.slug &&
	// 			// it's not should be redirected
	// 			!shouldSlugBeRedirected(path.params.slug) &&
	// 			// not excluded in sitemap
	// 			!path.params.excludeInSitemap &&
	// 			// not syllabus (since syllabus has its own page render)
	// 			path.params.navigationItem?.type !== 'syllabus' &&
	// 			// not the ones that have no layout
	// 			!!pageLayouts[path.params.navigationItem.type]
	// 		)
	// 	})
	// 	.map((path) => {
	// 		const params = {
	// 			slug: path.params.slug,
	// 		}

	// 		return {
	// 			params,
	// 		}
	// 	})

	// https://nextjs.org/docs/messages/ssg-fallback-true-export

	return {
		paths: [],
		// Set to false when exporting to static site
		fallback: 'blocking',
	}
}

export const getStaticProps: GetStaticProps = async ({
	params,
	preview = false,
	previewData = {},
}) => {
	const _props = await getPageStaticPropsForPath(params, preview, previewData)
	const page = _props?.data?.pageResponse?.item
	const requestedPageType = page?.system?.type

	// Remove all ace_ mappings if the page is not an ace page, except ace_group
	if (
		requestedPageType &&
		!requestedPageType.startsWith('wp_ace_') &&
		!requestedPageType.startsWith('ace_')
	) {
		_props.mappings = _props.mappings.filter((mapping) => {
			const pageType = mapping?.params?.navigationItem?.type
			return (
				pageType === 'ace_group' ||
				pageType === 'ace_subgroup' ||
				!pageType.startsWith('ace_')
			)
		})
	}

	// For Web Landing Page Redirect
	if (isWebLandingPage(page)) {
		const redirectUrl = page.elements.redirect_url.value
		if (redirectUrl) {
			return {
				redirect: {
					destination: redirectUrl,
					permanent: false,
				},
			}
		}
	}

	if (params.slug) {
		const slug = Array.isArray(params.slug) ? params.slug : [params.slug]
		const isHomepageRequest = slug.length === 1 && slug[0] === 'index'

		if (
			!_props ||
			!_props.data?.pageResponse?.item ||
			!pageLayouts?.[requestedPageType]
		) {
			// Fix: For homepage requests in preview mode, try to load homepage content instead of returning notFound
			if (isHomepageRequest && preview) {
				// Try to get homepage content directly by using empty slug for homepage
				try {
					const homepageProps = await getPageStaticPropsForPath(
						{ slug: [] },
						preview,
						previewData,
					)

					if (homepageProps?.data?.pageResponse?.item) {
						const cleanedProps = homepageProps
							? cleanJson(homepageProps)
							: {}
						return {
							props: {
								...cleanedProps,
								params: {
									...params,
									slug: [], // Use empty slug for homepage to fix breadcrumb
								},
								preview,
								previewData,
							},
						}
					}
				} catch (error) {
					console.error(
						'Failed to load homepage content in preview mode:',
						error,
					)
				}

				return {
					redirect: {
						destination: '/',
						permanent: false,
					},
				}
			}

			return {
				redirect: {
					destination: `/404?path=${slug.join('/')}`,
					permanent: false,
				},
			}
		}

		if (shouldSlugBeRedirected(slug)) {
			return {
				redirect: {
					destination: '/',
					permanent: false,
				},
			}
		}
	}

	const props = _props ? cleanJson(_props) : {}
	return {
		props: {
			...props,
			params,
			preview,
			previewData,
		},
	}
}

export default Page
